import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Quote } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BUSINESS } from "../../data/content";
import { Reveal } from "./Reveal";

export const About = () => {
  const navigate = useNavigate();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section id="about" data-testid="about-section" className="surface-light py-24 lg:py-36">
      <div className="mx-auto w-[92%] max-w-[1280px]">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <Reveal>
              <span className="font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-accent">About F&amp;C</span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-display font-bold tracking-tight leading-[0.98] text-4xl md:text-6xl text-fc-ink">
                Professional HVAC Service. Dependable Results.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-fc-ink2 text-lg max-w-xl">
                F&amp;C Air Conditioning provides professional air conditioning and HVAC solutions
                for residential and commercial customers.
              </p>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-4 text-fc-ink2 text-lg max-w-xl">
                Our services include sales, installation, repairs and service, along with electrical
                and walk-in cooler solutions.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <button
                data-testid="about-contact-btn"
                onClick={() => navigate("/contact")}
                className="group mt-8 inline-flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-accent"
              >
                Talk to F&amp;C Air Conditioning
                <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
            </Reveal>
          </div>

          {/* License card */}
          <Reveal delay={0.1} className="min-w-0">
            <div data-testid="license-card" className="rounded-2xl border border-fc-sky/25 p-6 sm:p-8 lg:p-12 shadow-[0_30px_70px_rgba(11,44,92,0.10)]" style={{ background: "linear-gradient(160deg,#f2f8ff,#e9f2fb)" }}>
              <div className="font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-fc-navy">{BUSINESS.name}</div>
              <div className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-bold text-fc-orange tracking-tight break-words">{BUSINESS.license}</div>
              <div className="mt-6 font-display text-2xl md:text-3xl font-semibold text-fc-navy leading-tight">
                Florida Class A<br />Air Conditioning Contractor
              </div>
              <div className="mt-6 flex items-center gap-2 font-mono text-sm font-bold uppercase tracking-widest text-fc-sky">
                <ShieldCheck size={16} /> Licensed &amp; Insured
              </div>
            </div>
          </Reveal>
        </div>

        {/* Founder band */}
        <div data-testid="founder-band" className="mt-24 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5" ref={imgRef}>
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-br from-fc-orange/15 to-fc-sky/10 blur-2xl" />
              <div className="relative aspect-[4/5] overflow-hidden border border-fc-ink/10 shadow-[0_30px_80px_rgba(11,44,92,0.18)]">
                <motion.img
                  src="/owner.webp"
                  alt={`${BUSINESS.owner}, Founder of F&C Air Conditioning`}
                  style={{ y: photoY }}
                  className="absolute inset-0 h-[112%] w-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-5 left-5 bg-fc-navy text-white px-5 py-3">
                <div className="font-display text-lg font-semibold tracking-tight leading-none">{BUSINESS.owner}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-fc-sky mt-1">Founder &amp; Owner</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <span className="inline-flex items-center gap-2 font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-accent">
                <ShieldCheck size={14} /> Meet the Owner
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <Quote size={40} className="text-fc-orange/30 mt-6" />
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-4 font-display text-2xl md:text-3xl font-medium tracking-tight leading-snug text-fc-ink max-w-2xl">
                “We treat every home and business like our own — with honest work,
                clear answers, and comfort you can count on, season after season.”
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-fc-ink2 max-w-xl">
                F&amp;C Air Conditioning is owned and operated by {BUSINESS.owner} and qualified
                Florida Certified Class A Air Conditioning Contractor, License #{BUSINESS.license}.
                Our business is built on hands-on expertise, accountability, and reliable HVAC service.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <button
                data-testid="founder-contact-btn"
                onClick={() => navigate("/contact")}
                className="group mt-8 inline-flex items-center gap-2 bg-fc-orange text-white px-7 py-4 font-bold uppercase tracking-wide text-sm hover:brightness-95 transition-all"
              >
                Work with {BUSINESS.owner.split(" ")[0]}
                <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
